import { ALLOWED_IMAGE_TYPES, IMAGE_MAX_BYTES } from '#shared/constants';

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Could not process image'));
          return;
        }
        resolve(blob);
      },
      type,
      quality,
    );
  });

/** Downscale an image file in the browser before upload */
export const resizeImageFile = async (
  file: File,
  maxWidth = 1600,
): Promise<{ blob: Blob; contentType: string }> => {
  if (
    !ALLOWED_IMAGE_TYPES.includes(
      file.type as (typeof ALLOWED_IMAGE_TYPES)[number],
    )
  ) {
    throw new Error('Please choose a JPEG, PNG, or WebP image.');
  }

  if (file.size > IMAGE_MAX_BYTES) {
    throw new Error('Image must be under 5 MB.');
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('Could not load image');
  }

  try {
    const scale = Math.min(1, maxWidth / bitmap.width);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not process image');
    }
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await canvasToBlob(canvas, file.type, 0.85);
    return { blob, contentType: file.type };
  } finally {
    bitmap.close();
  }
};

export const uploadToPresignedPost = async (
  url: string,
  fields: Record<string, string>,
  blob: Blob,
  contentType: string,
): Promise<void> => {
  if (url === 'local-dummy-upload') {
    return;
  }

  const formData = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    formData.append(key, value);
  }

  const subtype = contentType.split('/')[1];
  const extension = subtype === 'jpeg' ? 'jpg' : (subtype ?? 'bin');
  formData.append('file', blob, `upload.${extension}`);

  const response = await fetch(url, { method: 'POST', body: formData });
  if (!response.ok) {
    throw new Error('Image upload failed');
  }
};
